/**
 * The real, visible height of the screen — published as a CSS variable.
 *
 * `100vh` is the wrong number on a phone. It means "the viewport with the browser's address bar
 * hidden", which is not what the operator is looking at: with the bar showing, a `100vh` box is
 * taller than the screen, and whatever sits at its bottom edge — on this console, the reply box —
 * is pushed underneath the browser chrome. `100dvh` fixes that part, and is used as the fallback
 * here, but it still does not account for the on-screen keyboard: on iOS the keyboard does not
 * resize the layout viewport at all, so the moment an operator taps the composer to answer a
 * customer, the composer disappears behind the keyboard they just opened.
 *
 * `window.visualViewport` is the only thing that reports what is actually visible, keyboard
 * included. We read it and publish:
 *
 *   --npe-app-h   the visible height, for anything that wants to fill the screen
 *   --npe-kb      how much of the bottom is currently covered by the keyboard
 *
 * Both are plain CSS variables so components can use them in ordinary style rules and desktop
 * behaviour is unchanged (there, visualViewport.height === innerHeight and --npe-kb is 0).
 */

const setVar = (name, value) => {
    document.documentElement.style.setProperty(name, value);
};

const clearVar = (name) => {
    document.documentElement.style.removeProperty(name);
};

const apply = () => {
    const vv = window.visualViewport;
    const height = (vv && vv.height) || window.innerHeight || 0;

    // A measurement of zero is not a measurement.
    //
    // A page can be laid out while it has no visible viewport at all — rendered in a background
    // tab, restored from the back/forward cache, or in a hidden frame — and both visualViewport
    // and innerHeight report 0 there. Publishing that zero is worse than publishing nothing:
    // `var(--npe-app-h, 100dvh)` only falls back to 100dvh when the variable is *absent*, so a
    // variable set to 0px is a variable that has silently collapsed every screen that trusts it,
    // with the fallback standing right there unused. So remove it and let the fallback do its job
    // until there is a real number to publish. Caught on production, where the console reported
    // --npe-app-h: 0px on first paint.
    if (!(height > 0)) {
        clearVar("--npe-app-h");
        clearVar("--npe-kb");
        return;
    }

    // What the keyboard covers: the gap between the layout viewport and the visual one. Clamped at
    // zero because on desktop, and while the address bar animates, this can go slightly negative.
    const keyboard = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;

    setVar("--npe-app-h", `${Math.round(height)}px`);
    setVar("--npe-kb", `${Math.round(keyboard)}px`);
};

/**
 * Start tracking. Safe to call more than once; safe on browsers without visualViewport, where it
 * degrades to innerHeight and the CSS fallbacks take over.
 */
export const initViewportTracking = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    apply();

    const vv = window.visualViewport;
    if (vv) {
        // `scroll` matters as much as `resize`: on iOS the visual viewport slides rather than
        // resizing when the address bar collapses.
        vv.addEventListener("resize", apply);
        vv.addEventListener("scroll", apply);
    }
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", () => {
        // The new dimensions are not readable in the same tick as the event on iOS.
        setTimeout(apply, 250);
    });

    // The two ways a page comes back with dimensions it did not have when it was laid out. A tab
    // becoming visible does not reliably fire `resize`, so without these a console that was opened
    // in the background would keep the fallback height until something else happened to move.
    window.addEventListener("pageshow", apply);
    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) {
            apply();
        }
    });
};

export default initViewportTracking;
